import type { FetchResponse } from 'openapi-fetch';

type MediaType = `${string}/${string}`;
export function dataOrNull<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string | number, any>,
  TOptions,
  TMedia extends MediaType,
>(response: FetchResponse<T, TOptions, TMedia>) {
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data ?? null;
}

export function nestedDataOrNull<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string | number, any>,
  TOptions,
  TMedia extends MediaType,
>(response: FetchResponse<T, TOptions, TMedia>) {
  return pickDataOrNull(dataOrNull(response));
}

type PickDataOrNull<T> = T extends { data: infer U } ? U : null;

function pickDataOrNull<T>(data: T): PickDataOrNull<T> {
  if (data && typeof data === 'object' && 'data' in data) {
    return data.data as PickDataOrNull<T>;
  }
  return null as PickDataOrNull<T>;
}

/* Use when you want to throw an error if data is missing, instead of returning null */
export function requireData<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string | number, any>,
  TOptions,
  TMedia extends MediaType,
>(response: FetchResponse<T, TOptions, TMedia>) {
  // Reuses dataOrNull so a `response.error` (if present) surfaces in the
  // thrown error message; only falls back to the generic message below when
  // there's genuinely no data and no error (e.g. an unexpected empty body).
  const data = dataOrNull(response);
  if (data == null) {
    throw new Error('Data is null');
  }
  return data;
}
